import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Table, Badge, Spinner, Button, Form } from 'react-bootstrap';
import {
  useListEntrepreneurInvoicesQuery,
  useListEntrepreneurActivationInvoicesQuery,
  useListEntrepreneurFirmsQuery,
  useGetEntrepreneurAnnualSeatFeeQuery,
  useSetEntrepreneurAnnualSeatFeeMutation,
  useGetEntrepreneurMaxUserLimitQuery,
  useSetEntrepreneurMaxUserLimitMutation,
  useSyncEntrepreneurStripeCustomersMutation,
} from '../../app/api';
import { brand, usd, cardStyle, headStyle, EntPage, FilterBar, filterInputStyle, CombinedChargeNote } from './common';

const INVOICE_STATUSES = ['DRAFT', 'PAID', 'FAILED', 'VOID'];

const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString('en-US', {
  month: '2-digit', day: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
}) : '—');

function StatusBadge({ status }) {
  return <Badge bg={status === 'PAID' ? 'success' : status === 'FAILED' ? 'danger' : 'secondary'}>{status}</Badge>;
}
StatusBadge.propTypes = { status: PropTypes.string };

// Anthropic's per-seat annual license fee, passed through to firms — editable
// here since Anthropic can change their pricing. Shown live wherever it's
// mentioned to firms (currently the B2B Members tab).
function AnnualSeatFeeSetting() {
  const { data, isLoading } = useGetEntrepreneurAnnualSeatFeeQuery();
  const [setFee, { isLoading: saving }] = useSetEntrepreneurAnnualSeatFeeMutation();
  const [editing, setEditing] = useState(false);
  const [input, setInput] = useState('');
  const [error, setError] = useState(null);

  const startEdit = () => {
    setInput(data ? (data.cents / 100).toFixed(2) : '');
    setError(null);
    setEditing(true);
  };

  const save = async () => {
    const dollars = parseFloat(input);
    if (!Number.isFinite(dollars) || dollars < 0) {
      setError('Enter a valid, non-negative amount.');
      return;
    }
    try {
      await setFee({ cents: Math.round(dollars * 100) }).unwrap();
      setEditing(false);
    } catch (err) {
      setError(err?.data?.detail || 'Could not save.');
    }
  };

  return (
    <Card style={cardStyle} className="mb-3">
      <Card.Header style={headStyle}>Annual per-seat license fee</Card.Header>
      <Card.Body>
        <p style={{ color: brand.charcoal, fontSize: '0.82rem', marginBottom: '0.75rem' }}>
          Anthropic&apos;s per-user annual license cost, passed straight through to each firm — shown on their Members tab. Update this if Anthropic changes their pricing.
        </p>
        {isLoading ? <Spinner size="sm" /> : editing ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span>$</span>
            <Form.Control
              type="number" min="0" step="0.01" value={input} autoFocus disabled={saving}
              onChange={(e) => setInput(e.target.value)} style={{ width: 120 }}
            />
            <span style={{ color: brand.charcoal, fontSize: '0.85rem' }}>/ user / year</span>
            <Button size="sm" variant="success" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
            <Button size="sm" variant="outline-secondary" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{ fontWeight: 700, color: brand.deepBlue, fontSize: '1.1rem' }}>{usd(data?.cents || 0)} / user / year</span>
            <Button size="sm" variant="outline-primary" onClick={startEdit}>Edit</Button>
          </div>
        )}
        {error && <div style={{ color: brand.red, fontSize: '0.8rem', marginTop: '0.5rem' }}>{error}</div>}
      </Card.Body>
    </Card>
  );
}

// The ceiling a firm admin can self-set on any one member's monthly Claude
// spend limit. Firm admins set members' limits directly (pushed to Claude via
// the Spend Limits API); this caps how high they can go without involving PCA.
function MaxUserLimitSetting() {
  const { data, isLoading } = useGetEntrepreneurMaxUserLimitQuery();
  const [setMax, { isLoading: saving }] = useSetEntrepreneurMaxUserLimitMutation();
  const [editing, setEditing] = useState(false);
  const [input, setInput] = useState('');
  const [error, setError] = useState(null);

  const startEdit = () => {
    setInput(data ? (data.cents / 100).toFixed(2) : '');
    setError(null);
    setEditing(true);
  };

  const save = async () => {
    const dollars = parseFloat(input);
    if (!Number.isFinite(dollars) || dollars <= 0) {
      setError('Enter a valid amount greater than $0.');
      return;
    }
    try {
      await setMax({ cents: Math.round(dollars * 100) }).unwrap();
      setEditing(false);
    } catch (err) {
      setError(err?.data?.detail || 'Could not save.');
    }
  };

  return (
    <Card style={cardStyle} className="mb-3">
      <Card.Header style={headStyle}>Max per-member spend limit</Card.Header>
      <Card.Body>
        <p style={{ color: brand.charcoal, fontSize: '0.82rem', marginBottom: '0.75rem' }}>
          The highest monthly Claude spend limit a firm admin can set for one of their members on their own. Limit changes below this apply to Claude immediately; a firm must contact us to go higher.
        </p>
        {isLoading ? <Spinner size="sm" /> : editing ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span>$</span>
            <Form.Control
              type="number" min="0" step="0.01" value={input} autoFocus disabled={saving}
              onChange={(e) => setInput(e.target.value)} style={{ width: 120 }}
            />
            <span style={{ color: brand.charcoal, fontSize: '0.85rem' }}>/ user / month</span>
            <Button size="sm" variant="success" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
            <Button size="sm" variant="outline-secondary" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{ fontWeight: 700, color: brand.deepBlue, fontSize: '1.1rem' }}>{usd(data?.cents || 0)} / user / month</span>
            <Button size="sm" variant="outline-primary" onClick={startEdit}>Edit</Button>
          </div>
        )}
        {error && <div style={{ color: brand.red, fontSize: '0.8rem', marginTop: '0.5rem' }}>{error}</div>}
      </Card.Body>
    </Card>
  );
}

// Renames each firm's Stripe customer to the firm's name (older customers were
// created as "firm-{id}"). Idempotent, so it's also how the rename gets
// backfilled in each environment - run it once after deploying.
function StripeCustomerSync() {
  const [sync, { isLoading }] = useSyncEntrepreneurStripeCustomersMutation();
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const run = async () => {
    setError(null);
    try {
      setResult(await sync().unwrap());
    } catch (err) {
      setError(err?.data?.detail || 'Sync failed.');
    }
  };

  return (
    <Card style={cardStyle} className="mb-3">
      <Card.Header style={headStyle}>Stripe customer names</Card.Header>
      <Card.Body>
        <p style={{ color: brand.charcoal, fontSize: '0.82rem', marginBottom: '0.75rem' }}>
          Sets each firm&apos;s Stripe customer name to the firm&apos;s name so charges are easy to find in Stripe. New firms are named automatically; this updates older ones. Safe to run any time.
        </p>
        <Button size="sm" variant="outline-primary" onClick={run} disabled={isLoading}>
          {isLoading ? <><Spinner size="sm" className="me-1" />Syncing…</> : 'Sync names to Stripe'}
        </Button>
        {result && (
          <div style={{ color: brand.charcoal, fontSize: '0.8rem', marginTop: '0.6rem' }}>
            Checked {result.checked}, renamed {result.changed}.
            {result.results.filter(r => r.changed).map(r => (
              <div key={r.firm_id}>{r.old_name || '(blank)'} → {r.firm_name}</div>
            ))}
            {result.failed.map(r => (
              <div key={r.firm_id} style={{ color: brand.red }}>{r.firm_name}: {r.error}</div>
            ))}
          </div>
        )}
        {error && <div style={{ color: brand.red, fontSize: '0.8rem', marginTop: '0.5rem' }}>{error}</div>}
      </Card.Body>
    </Card>
  );
}

export default function Invoices() {
  const { data: invoices = [], isLoading } = useListEntrepreneurInvoicesQuery();
  const { data: activations = [], isLoading: activationsLoading } = useListEntrepreneurActivationInvoicesQuery();
  // Firm list resolves invoice firm_id -> name (this page no longer sits next
  // to the sign-ups table, which used to supply those names).
  const { data: firms = [] } = useListEntrepreneurFirmsQuery();
  const firmNameById = useMemo(() => {
    const m = {};
    firms.forEach(f => { m[f.id] = f.name; });
    return m;
  }, [firms]);

  const [usageSearch, setUsageSearch] = useState('');
  const [usageStatus, setUsageStatus] = useState('');
  const filteredInvoices = useMemo(() => {
    const q = usageSearch.trim().toLowerCase();
    return invoices.filter(inv => {
      if (usageStatus && inv.status !== usageStatus) return false;
      if (q && !(firmNameById[inv.firm_id] || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [invoices, usageSearch, usageStatus, firmNameById]);

  const [actSearch, setActSearch] = useState('');
  const [actStatus, setActStatus] = useState('');
  const filteredActivations = useMemo(() => {
    const q = actSearch.trim().toLowerCase();
    return activations.filter(inv => {
      if (actStatus && inv.status !== actStatus) return false;
      if (q && !`${firmNameById[inv.firm_id] || ''} ${inv.member_email || ''} ${inv.note || ''}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [activations, actSearch, actStatus, firmNameById]);

  return (
    <EntPage title="Invoices" subtitle="Monthly metered usage and one-time seat activation charges billed to each firm's card.">
      <AnnualSeatFeeSetting />
      <MaxUserLimitSetting />
      <StripeCustomerSync />

      <Card style={cardStyle} className="mb-3">
        <Card.Header style={{ ...headStyle, display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span>Monthly usage invoices</span>
          <FilterBar search={usageSearch} onSearchChange={setUsageSearch} placeholder="Search firm…">
            <select value={usageStatus} onChange={(e) => setUsageStatus(e.target.value)} aria-label="Status" style={filterInputStyle}>
              <option value="">Any status</option>
              {INVOICE_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </FilterBar>
        </Card.Header>
        <Card.Body className="p-0">
          {isLoading ? <div className="text-center p-4"><Spinner size="sm" /></div> : filteredInvoices.length === 0 ? (
            <div style={{ padding: '1.5rem', color: brand.charcoal, fontSize: '0.85rem' }}>{invoices.length === 0 ? 'No invoices yet.' : 'No invoices match these filters.'}</div>
          ) : (
            <Table responsive className="mb-0" style={{ fontSize: '0.85rem' }}>
              <thead><tr style={{ color: brand.charcoal }}>
                <th style={{ padding: '0.6rem 0.9rem' }}>Firm</th>
                <th style={{ padding: '0.6rem 0.9rem' }}>Period</th>
                <th style={{ padding: '0.6rem 0.9rem', textAlign: 'right' }}>Usage</th>
                <th style={{ padding: '0.6rem 0.9rem', textAlign: 'right' }}>Fee</th>
                <th style={{ padding: '0.6rem 0.9rem', textAlign: 'right' }}>Total</th>
                <th style={{ padding: '0.6rem 0.9rem' }}>Status</th>
              </tr></thead>
              <tbody>
                {filteredInvoices.map(inv => (
                  <tr key={inv.id}>
                    <td style={{ padding: '0.5rem 0.9rem', verticalAlign: 'middle', color: brand.charcoal }}>{firmNameById[inv.firm_id] || `#${inv.firm_id}`}</td>
                    <td style={{ padding: '0.5rem 0.9rem', verticalAlign: 'middle' }}>
                      {inv.period_year}-{String(inv.period_month).padStart(2, '0')}
                      <CombinedChargeNote combined={inv.combined_charge} status={inv.status} />
                    </td>
                    <td style={{ padding: '0.5rem 0.9rem', verticalAlign: 'middle', textAlign: 'right', color: brand.charcoal }}>{usd(inv.usage_cost_cents)}</td>
                    <td style={{ padding: '0.5rem 0.9rem', verticalAlign: 'middle', textAlign: 'right', color: brand.charcoal }}>{usd(inv.fee_cents)}</td>
                    <td style={{ padding: '0.5rem 0.9rem', verticalAlign: 'middle', textAlign: 'right', fontWeight: 700, color: brand.deepBlue }}>{usd(inv.total_charged_cents)}</td>
                    <td style={{ padding: '0.5rem 0.9rem', verticalAlign: 'middle' }}><StatusBadge status={inv.status} /></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Card style={cardStyle}>
        <Card.Header style={{ ...headStyle, display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span>Seat activation charges</span>
          <FilterBar search={actSearch} onSearchChange={setActSearch} placeholder="Search firm, member or note…">
            <select value={actStatus} onChange={(e) => setActStatus(e.target.value)} aria-label="Status" style={filterInputStyle}>
              <option value="">Any status</option>
              {INVOICE_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </FilterBar>
        </Card.Header>
        <Card.Body className="p-0">
          {activationsLoading ? <div className="text-center p-4"><Spinner size="sm" /></div> : filteredActivations.length === 0 ? (
            <div style={{ padding: '1.5rem', color: brand.charcoal, fontSize: '0.85rem' }}>{activations.length === 0 ? 'No seat activation charges yet.' : 'No charges match these filters.'}</div>
          ) : (
            <Table responsive className="mb-0" style={{ fontSize: '0.85rem' }}>
              <thead><tr style={{ color: brand.charcoal }}>
                <th style={{ padding: '0.6rem 0.9rem' }}>Firm</th>
                <th style={{ padding: '0.6rem 0.9rem' }}>Member</th>
                <th style={{ padding: '0.6rem 0.9rem' }}>Note</th>
                <th style={{ padding: '0.6rem 0.9rem' }}>Date</th>
                <th style={{ padding: '0.6rem 0.9rem', textAlign: 'right' }}>Base</th>
                <th style={{ padding: '0.6rem 0.9rem', textAlign: 'right' }}>Tax</th>
                <th style={{ padding: '0.6rem 0.9rem', textAlign: 'right' }}>Fee</th>
                <th style={{ padding: '0.6rem 0.9rem', textAlign: 'right' }}>Total</th>
                <th style={{ padding: '0.6rem 0.9rem' }}>Status</th>
              </tr></thead>
              <tbody>
                {filteredActivations.map(inv => (
                  <tr key={inv.id}>
                    <td style={{ padding: '0.5rem 0.9rem', verticalAlign: 'middle', color: brand.charcoal }}>{firmNameById[inv.firm_id] || `#${inv.firm_id}`}</td>
                    <td style={{ padding: '0.5rem 0.9rem', verticalAlign: 'middle' }}>{inv.member_email || '—'}</td>
                    <td style={{ padding: '0.5rem 0.9rem', verticalAlign: 'middle', color: brand.charcoal, maxWidth: 260, overflowWrap: 'anywhere' }}>{inv.note || '—'}</td>
                    <td style={{ padding: '0.5rem 0.9rem', verticalAlign: 'middle', color: brand.charcoal, whiteSpace: 'nowrap' }}>{fmtDateTime(inv.created)}</td>
                    <td style={{ padding: '0.5rem 0.9rem', verticalAlign: 'middle', textAlign: 'right', color: brand.charcoal }}>{usd(inv.base_cents)}</td>
                    <td style={{ padding: '0.5rem 0.9rem', verticalAlign: 'middle', textAlign: 'right', color: brand.charcoal }}>{usd(inv.tax_cents)}</td>
                    <td style={{ padding: '0.5rem 0.9rem', verticalAlign: 'middle', textAlign: 'right', color: brand.charcoal }}>{usd(inv.fee_cents)}</td>
                    <td style={{ padding: '0.5rem 0.9rem', verticalAlign: 'middle', textAlign: 'right', fontWeight: 700, color: brand.deepBlue }}>{usd(inv.total_charged_cents)}</td>
                    <td style={{ padding: '0.5rem 0.9rem', verticalAlign: 'middle' }}><StatusBadge status={inv.status} /></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </EntPage>
  );
}
